/**
 * Règle d'ordre des mails du Focus, selon le mode de tri de l'utilisateur.
 *
 * L'API trie le Focus ; le SDK n'applique cette règle qu'aux mails qui entrent au Focus en direct,
 * entre deux chargements.
 *
 * ⚠️ Fichier miroir : `Mailvista-Api-Backend/src/functions/domains/smartLabel/utils/focusMailOrdering.utils.ts`
 * La règle DOIT être identique des deux côtés : une divergence fait sauter un mail de place au
 * rechargement. Tout changement ici doit être porté là-bas, et réciproquement.
 *
 * Départage par identifiant croissant en unités de code UTF-16 (comparaison `<` native), PAS
 * `localeCompare` : sous Hermes sans ICU, « B » précède « a » ; avec ICU, l'inverse.
 */

import type { FocusSortMode } from "../user/types/settings";
import type { SmartLabelEntry } from "./types/customViews";

const byInternetMessageId = (a: SmartLabelEntry, b: SmartLabelEntry): number =>
    a.id < b.id ? -1 : a.id > b.id ? 1 : 0;

/** Date en millisecondes ; absente ou illisible → `null`. */
const toTime = (date: Date | string | null | undefined): number | null => {
    const time =
        date === null || date === undefined ? NaN : new Date(date).getTime();
    return Number.isNaN(time) ? null : time;
};

/** Tri décroissant d'une date, date absente ou illisible en dernier ; 0 si égales. */
const compareDateDesc = (
    a: Date | string | null | undefined,
    b: Date | string | null | undefined,
): number => {
    const aTime = toTime(a);
    const bTime = toTime(b);

    if (aTime === bTime) return 0;
    if (aTime === null) return 1;
    if (bTime === null) return -1;
    return bTime - aTime;
};

/**
 * Ordre historique : mails Mailvista, puis mis en Focus à la main (le plus récemment d'abord), puis score.
 * Score absent compté comme 0 : l'API lit déjà un score NULL comme 0.
 */
const compareByImportance = (a: SmartLabelEntry, b: SmartLabelEntry): number =>
    compareDateDesc(a.sentDateTimeMailvista, b.sentDateTimeMailvista) ||
    compareDateDesc(a.manualFocusDate, b.manualFocusDate) ||
    (b.score ?? 0) - (a.score ?? 0);

/** Plus récent en haut ; les mails Mailvista et mis en Focus à la main ne sont pas épinglés. */
const compareByDate = (a: SmartLabelEntry, b: SmartLabelEntry): number =>
    compareDateDesc(a.date, b.date);

/** Négatif si `a` passe avant `b` dans le Focus trié selon `focusSortMode`. */
export const compareFocusEntries = (
    a: SmartLabelEntry,
    b: SmartLabelEntry,
    focusSortMode: FocusSortMode,
): number =>
    (focusSortMode === "DATE"
        ? compareByDate(a, b)
        : compareByImportance(a, b)) || byInternetMessageId(a, b);

/** Ordonne le Focus selon le mode demandé. Ne modifie pas le tableau reçu. */
export const sortFocusEntries = (
    entries: readonly SmartLabelEntry[],
    focusSortMode: FocusSortMode,
): SmartLabelEntry[] =>
    [...entries].sort((a, b) => compareFocusEntries(a, b, focusSortMode));
